import { isWeekend } from "Infrastructure/Utilities/Helpers/dateUtils";
import { PartnerPaths } from "Partner/Routes/Routing";
import {
	Box,
	Chip,
	Link as MuiLink,
	Stack,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { Link } from "react-router-dom";
import { DayCell } from "./DayCell";
import {
	type PartnerCalendarRowData,
	PartnerCalendarStatus,
	type PartnerVerticalWithLevel,
} from "./types";

const LEVEL_EMOJI: Record<string, string> = {
	Gold: "\u{1F947}",
	Silver: "\u{1F948}",
	Bronze: "\u{1F949}",
	Adam: "\u{1F535}",
};

const SHORT_VERTICAL_NAME: Record<string, string> = {
	Wallpapers: "Wallpaper",
	"Exterior painting": "Exterior",
	"Interior painting": "Interior",
	Electricians: "Electric",
	Flooring: "Floors",
	Renovations: "Reno",
};

function formatVerticalsWithLevel(
	verticals: PartnerVerticalWithLevel[],
): string {
	return verticals
		.map((v) => {
			const emoji = LEVEL_EMOJI[v.level] ?? "";
			const name = SHORT_VERTICAL_NAME[v.name] ?? v.name;
			return emoji ? `${name} (${emoji})` : name;
		})
		.join(", ");
}

type PartnerRowProps = {
	row: PartnerCalendarRowData;
	days: number[];
	startDate: Dayjs;
	onToggleAvailability: (
		partnerId: number,
		date: string,
		currentStatus?: PartnerCalendarStatus,
	) => void;
};

export function PartnerRow({
	row,
	days,
	startDate,
	onToggleAvailability,
}: PartnerRowProps) {
	const { partner } = row;
	return (
		<TableRow hover sx={{ "&:hover td": { backgroundColor: "action.hover" } }}>
			<TableCell
				sx={{
					verticalAlign: "middle",
					borderRight: "1px solid #e0e0e0",
					position: { xs: "static", md: "sticky" },
					left: { xs: "auto", md: 0 },
					p: 0,
					backgroundColor: "background.default",
					zIndex: 5,
					boxShadow: {
						xs: "none",
						md: "2px 0 5px -2px rgba(0,0,0,0.1)",
					},
					"&:hover": {
						backgroundColor: "background.default",
					},
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					spacing={3}
					sx={{
						p: 1.5,
						height: 60, // Match cell height for consistency
						backgroundColor: "background.default",
					}}
				>
					<Box sx={{ width: 150 }}>
						<MuiLink
							component={Link}
							to={PartnerPaths.Detail.path(partner.id)}
							underline="hover"
							sx={{ color: "text.primary", lineHeight: 1.2 }}
						>
							<Typography
								variant="subtitle2"
								fontWeight="600"
								sx={{ lineHeight: 1.2 }}
							>
								{partner.name}
							</Typography>
						</MuiLink>
						<Typography
							variant="caption"
							color="text.secondary"
							display="block"
							sx={{ fontSize: "0.7rem" }}
						>
							{formatVerticalsWithLevel(partner.verticals)}
						</Typography>
					</Box>

					<Stack spacing={0} sx={{ minWidth: 150 }}>
						{partner.email && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", fontSize: "0.7rem" }}
							>
								{partner.email}
							</Typography>
						)}
						{partner.phone && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", fontSize: "0.7rem" }}
							>
								{partner.phone}
							</Typography>
						)}
					</Stack>

					<Stack direction="row" spacing={1}>
						<Tooltip title="Confirmed Jobs">
							<Chip
								label={partner.confirmedJobsCount}
								size="small"
								variant="outlined"
								sx={{
									height: 20,
									minWidth: 30,
									fontSize: "0.75rem",
									fontWeight: 600,
									borderColor: "#FBC02D", // Yellow
									color: "#FBC02D",
								}}
							/>
						</Tooltip>
						<Tooltip title="In Progress Jobs">
							<Chip
								label={partner.inProgressJobsCount}
								size="small"
								variant="outlined"
								sx={{
									height: 20,
									minWidth: 30,
									fontSize: "0.75rem",
									fontWeight: 600,
									borderColor: "#F57C00", // Orange
									color: "#F57C00",
								}}
							/>
						</Tooltip>
					</Stack>
				</Stack>
			</TableCell>
			{days.map((day) => {
				const dateObj = startDate.date(day);
				const dayDate = dateObj.format("YYYY-MM-DD");
				const isWeekendDay = isWeekend(dateObj);
				const isToday = dateObj.isSame(dayjs(), "day");
				const availability = row.availability[dayDate];
				const isOccupied = availability?.status === PartnerCalendarStatus.ADAM;

				return (
					<DayCell
						key={day}
						availability={availability}
						isWeekend={isWeekendDay}
						isToday={isToday}
						onClick={
							isOccupied
								? undefined
								: () =>
										onToggleAvailability(
											row.partner.id,
											dayDate,
											availability?.status,
										)
						}
					/>
				);
			})}
		</TableRow>
	);
}
